import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useClient } from '../connect_api';

import {
    MachineService,
    ProductService,
    ProductMap,
    TrackableItemState,
    Customer,
    EventType,
    Event,
    OrganizationService,
    VirtualProductManagementService,
    MachineVariantConfig,
    VirtualProductMode,
} from '../api/api_pb';
import { VirtualProductCategory } from '../kiosk/v1/virtual_products_pb';
import RelativeTime from '../components/RelativeTime';

import StateUpdateModal from '../components/StateUpdateModal';
import CurrencyFormatter from '../utils/currencyFormatter';
import BindingProductAndTackableItemModal from '../components/BindingProductAndTackableItemModal';
import { useGlobalContext } from '../GlobalContext';
import { useMachineByUuid } from '../hooks/useMachineByUuid';
import ConfirmModal from "../components/ConfirmModal";
import { Loader2, Save, Package, ChevronDown, ChevronRight, CreditCard } from 'lucide-react';

const trackableItemStateMap: Record<number, string> = {
    0: 'Unspecified',
    1: 'Ready',
    2: 'In Use',
    3: 'Pending Inspection',
    4: 'Returned',
};

const LockerDetails: React.FC = () => {
    const { machineUuid } = useParams();
    const navigate = useNavigate();
    const { machine, isLoading: machineLoading } = useMachineByUuid(machineUuid);
    const machineClient = useClient(MachineService);
    const productClient = useClient(ProductService);
    const [productMaps, setProductMaps] = useState<ProductMap[]>([]);
    const [showModal, setShowModal] = useState(false);
    const [currentState, setCurrentState] = useState<TrackableItemState>(TrackableItemState.READY);
    const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [productMap, setProductMap] = useState<ProductMap | null>(null);
    const { updateCustomers } = useGlobalContext();

    const [selectOpenItem,setSelectOpenItem] = useState<ProductMap|null>(null)

    // Virtual products state
    const organizationClient = useClient(OrganizationService);
    const virtualProductClient = useClient(VirtualProductManagementService);
    const [vpEnabled, setVpEnabled] = useState(false);
    const [vpMode, setVpMode] = useState<'external' | 'native' | null>(null);
    const [machineConfigs, setMachineConfigs] = useState<MachineVariantConfig[]>([]);
    const [enabledVariantUuids, setEnabledVariantUuids] = useState<Set<string>>(new Set());
    const [externalCategories, setExternalCategories] = useState<VirtualProductCategory[]>([]);
    const [expandedExternalCategories, setExpandedExternalCategories] = useState<Set<string>>(new Set());
    const [loadingVirtualProducts, setLoadingVirtualProducts] = useState(false);
    const [savingVirtualProducts, setSavingVirtualProducts] = useState(false);

    const fetchProductMaps = useCallback(async (machineUuid: string) => {
        try {
            const timeoutId = setTimeout(() => {
                toast.dismiss();
                toast.error('Request timed out');
            }, 10000);

            try {
                const response = await machineClient.listMachineProductMaps({ machineUuid });
                clearTimeout(timeoutId);
                if (response.productMaps.length !== 0) {
                    setProductMaps(response.productMaps);
                }
            } catch (e) {
                clearTimeout(timeoutId);
                toast.dismiss();
                toast.error('Failed to fetch data');
                console.error('error', e);
            }
        } catch (err) {
            toast.dismiss();
            toast.error('Failed to fetch data');
            console.error('error', err);
        }
    }, [machineClient]);

    const handleUpdateState = async (newState: TrackableItemState) => {
        if (selectedItemId && machine) {
            if (newState !== currentState) {
                try {
                    await productClient.updateTrackableItemState({
                        trackableItemUuid: selectedItemId,
                        state: newState,
                    });
                    fetchProductMaps(machine.uuid);
                    toast.dismiss();
                    toast.success('Update TrackableItemState Successfully');
                } catch (err) {
                    toast.dismiss();
                    toast.error('Failed to Update TrackableItem State');
                    console.error('Update TrackableItemState Error', err);
                }
            }
        }
    };

    const openDoor = (productMap:ProductMap) => {
        if (!machine) return;
        machineClient.openDoors({
            machineUuid: machine.uuid,
            productMapUuids: [productMap.uuid],
        }).then(res => {
            toast.dismiss();
            setSelectOpenItem(null)
            toast.success('Successfully sent door opening request');
        }).catch(err => {
            toast.dismiss();
            toast.error('Failed to send door opening request');
            console.error('OpenDoor Error', err);
        })
    };

    const handleShowleasesClick = (customer: Customer | undefined) => {
        if (customer) {
            updateCustomers(customer);
            navigate(`/show-lease/${customer.uuid}`);
        }
    };

    useEffect(() => {
        if (machine?.uuid) {
            fetchProductMaps(machine.uuid);
        }
    }, [machine, fetchProductMaps]);

    // Fetch virtual product config and data
    useEffect(() => {
        if (!machine?.uuid) return;

        const fetchVirtualProducts = async () => {
            setLoadingVirtualProducts(true);
            try {
                const orgResponse = await organizationClient.getMyOrganizationInfo({});
                const config = orgResponse.organizationInfo?.virtualProductConfig;
                if (!config?.enabled) {
                    setVpEnabled(false);
                    setLoadingVirtualProducts(false);
                    return;
                }

                setVpEnabled(true);
                const mode = config.mode === VirtualProductMode.NATIVE ? 'native' : 'external';
                setVpMode(mode);

                if (mode === 'native') {
                    const configResponse = await virtualProductClient.getMachineVariantConfigs({
                        machineUuid: machine.uuid,
                    });
                    setMachineConfigs(configResponse.configs);
                    setEnabledVariantUuids(new Set(
                        configResponse.configs.filter(c => c.enabled).map(c => c.variantUuid)
                    ));
                } else {
                    const previewResponse = await virtualProductClient.previewVirtualProducts({});
                    setExternalCategories(previewResponse.categories);
                    setExpandedExternalCategories(new Set(previewResponse.categories.map(c => c.name)));
                }
            } catch (error) {
                console.error('Failed to fetch virtual product config:', error);
            } finally {
                setLoadingVirtualProducts(false);
            }
        };

        fetchVirtualProducts();
    }, [machine, organizationClient, virtualProductClient]);

    const toggleVariantEnabled = (variantUuid: string) => {
        setEnabledVariantUuids(prev => {
            const next = new Set(prev);
            if (next.has(variantUuid)) {
                next.delete(variantUuid);
            } else {
                next.add(variantUuid);
            }
            return next;
        });
    };

    const handleSaveVirtualProductConfigs = async () => {
        if (!machine?.uuid) return;
        setSavingVirtualProducts(true);
        try {
            await virtualProductClient.updateMachineVariantConfigs({
                machineUuid: machine.uuid,
                enabledVariantUuids: Array.from(enabledVariantUuids),
            });
            toast.dismiss();
            toast.success('Virtual product configuration saved');
            const configResponse = await virtualProductClient.getMachineVariantConfigs({
                machineUuid: machine.uuid,
            });
            setMachineConfigs(configResponse.configs);
            setEnabledVariantUuids(new Set(
                configResponse.configs.filter(c => c.enabled).map(c => c.variantUuid)
            ));
        } catch (error) {
            console.error('Failed to save virtual product config:', error);
            toast.dismiss();
            toast.error('Failed to save virtual product configuration');
        } finally {
            setSavingVirtualProducts(false);
        }
    };

    const toggleExternalCategory = (name: string) => {
        setExpandedExternalCategories(prev => {
            const next = new Set(prev);
            if (next.has(name)) next.delete(name);
            else next.add(name);
            return next;
        });
    };

    const handleRefreshTable = () => {
        if (machine) fetchProductMaps(machine.uuid);
    };

    const handleSyncDoorsStatusBtn = () => {
        if (!machine) return;
        machineClient.sendEventToMachine({
            machineUuid: machine.uuid,
            event: { type: EventType.REPORT_DOORS_STATUS } as Event
        }).then(res => {
            setTimeout(() => {
                handleRefreshTable()
            }, 5000)
        }).catch(err => {
            toast.dismiss();
            toast.error('Failed to send sync doors status request');
        });
    };

    if (machineLoading) {
        return <div className="p-4 text-center">Loading machine details...</div>;
    }

    return (
        <div className="p-4">
            {/* Machine Details Table */}
            <table className="min-w-full table-auto border-collapse border border-gray-300">
                <thead>
                    <tr className="bg-gray-100">
                        <th className="border border-gray-300 text-left px-4 py-2">UUID</th>
                        <th className="border border-gray-300 text-left px-4 py-2">Name</th>
                        <th className="border border-gray-300 text-left px-4 py-2">Address</th>
                        <th className="border border-gray-300 text-left px-4 py-2">Description</th>
                        <th className="border border-gray-300 text-left px-4 py-2">Image URL</th>
                        <th className="border border-gray-300 text-left px-4 py-2">Pin Code</th>
                        <th className="border border-gray-300 text-left px-4 py-2">Contact Number</th>
                        <th className="border border-gray-300 text-left px-4 py-2">Created At</th>
                        <th className="border border-gray-300 text-left px-4 py-2">Updated At</th>
                        <th className="border border-gray-300 text-left px-4 py-2">Last Heartbeat At</th>
                        <th className="border border-gray-300 text-left px-4 py-2">Action</th>
                    </tr>
                </thead>
                <tbody>
                    {machine ? (
                        <tr key={machine.uuid}>
                            <td className="border border-gray-300 px-4 py-2 relative group">
                                <div className={`absolute left-0 top-0 h-full w-[8px] ${machine.isOnline ? "bg-green-500" : "bg-gray-400"}`}></div>
                                <span className="absolute left-2 top-1/2 -translate-y-1/2 hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap">{machine.isOnline ? "Online" : "Offline"}</span>
                                <span>{machine.uuid}</span>
                            </td>
                            <td className="border border-gray-300 px-4 py-2">{machine.name}</td>
                            <td className="border border-gray-300 px-4 py-2">{machine.address}</td>
                            <td className="border border-gray-300 px-4 py-2">{machine.description}</td>
                            <td className="border border-gray-300 px-4 py-2">
                                <img src={machine.imageUrl} alt={machine.name} className="w-20 h-20 object-contain" />
                            </td>
                            <td className="border border-gray-300 px-4 py-2">{machine.pinCode}</td>
                            <td className="border border-gray-300 px-4 py-2">{machine.contactNumber}</td>
                            <td className="border border-gray-300 px-4 py-2">
                                {machine.createdAt ? (
                                    <div className="text-sm">
                                        <div><RelativeTime timestamp={machine.createdAt} /></div>
                                    </div>
                                ) : ''}
                            </td>
                            <td className="border border-gray-300 px-4 py-2">
                                {machine.updatedAt ? (
                                    <div className="text-sm">
                                        <div><RelativeTime timestamp={machine.updatedAt} /></div>
                                    </div>
                                ) : ''}
                            </td>
                            <td className="border border-gray-300 px-4 py-2">
                                {machine.lastHeartbeatAt ? (
                                    <div className="text-sm">
                                        <div><RelativeTime timestamp={machine.lastHeartbeatAt} /></div>
                                    </div>
                                ) : ''}
                            </td>

                            <td className="border border-gray-300 px-4 py-2">
                                <div className="flex flex-wrap gap-2">
                                    <button className="btn btn-primary" onClick={handleSyncDoorsStatusBtn}>Sync Doors Status</button>
                                    <Link
                                        to={`/door-status-history/${machine.uuid}`}
                                        className="btn btn-info"
                                    >
                                        Door Status History
                                    </Link>
                                    <button
                                        className="btn btn-secondary inline-flex items-center gap-1"
                                        onClick={() => navigate(`/payment-settings/${machine.uuid}`, { state: { machine } })}
                                    >
                                        <CreditCard className="w-4 h-4" />
                                        Payment Settings
                                    </button>
                                </div>
                            </td>
                        </tr>
                    ) : (
                        <tr>
                            <td colSpan={11} className="border border-gray-300 px-4 py-2 text-center">No machine details available</td>
                        </tr>
                    )}
                </tbody>
            </table>

            {/* Product Maps Table */}
            <table className="min-w-full table-auto border-collapse border border-gray-300 mt-5">
                <thead>
                    <tr className="bg-gray-100">
                        <th className="border border-gray-300 text-left px-4 py-2">UUID</th>
                        <th className="border border-gray-300 text-left px-4 py-2">MDB Item Number</th>
                        <th className="border border-gray-300 text-left px-4 py-2">Door ID</th>
                        <th className="border border-gray-300 text-left px-4 py-2">Door Status</th>
                        <th className="border border-gray-300 text-left px-4 py-2">Product Name</th>
                        <th className="border border-gray-300 text-left px-4 py-2">Product Image</th>
                        <th className="border border-gray-300 text-left px-4 py-2">Product Price</th>
                        <th className="border border-gray-300 text-left px-4 py-2">Trackable Item State</th>
                        <th className="border text-left border-gray-300 p-2">Current Customer</th>
                        <th className="border border-gray-300 text-left px-4 py-2">Created / Updated</th>
                        <th className="border border-gray-300 text-left px-4 py-2">Action</th>
                    </tr>
                </thead>
                <tbody>
                    {productMaps.length === 0 ? (
                        <tr>
                            <td colSpan={11} className="border border-gray-300 px-4 py-2 text-center">No product maps available</td>
                        </tr>
                    ) : (
                        productMaps.map((map) => (
                            <tr key={map.uuid}>
                                <td className="border border-gray-300 px-4 py-2">{map.uuid}</td>
                                <td className="border border-gray-300 px-4 py-2">{map.mdbItemNumber.toString()}</td>
                                <td className="border border-gray-300 px-4 py-2">{map.doorIdDisplay}</td>
                                <td className="border border-gray-300 px-4 py-2">
                                    <p>status:{map.doorIsOpen ? <span className={'inline-block bg-blue-200 rounded-md px-1 py-1 text-sm font-semibold text-gray-700 mr-2'}>OPENED</span> : <span className={'inline-block bg-green-200 rounded-md px-1 py-1 text-sm font-semibold text-gray-700 mr-2'}>CLOSED</span>}</p>
                                    <p>updatedAt: {map.doorStatusLastUpdatedAt ? (
                                        <span className="text-sm">
                                            <span><RelativeTime timestamp={map.doorStatusLastUpdatedAt} /></span>
                                        </span>
                                    ) : ''}</p>
                                    {map.doorStatusLastUpdatedAt && (
                                        <Link
                                            to={`/door-status-history/${machine?.uuid}/${map.mdbItemNumber}`}
                                            className="text-blue-600 hover:text-blue-800 underline text-sm"
                                        >
                                            Door Status History
                                        </Link>
                                    )}
                                </td>
                                <td className="border border-gray-300 px-4 py-2">
                                    {map.product ? (
                                        <>
                                            <div className="flex items-center gap-2">
                                                <span>{map.product.name}</span>
                                                {map.product.forSale && (
                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                        For Sale
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-sm text-gray-600">TrackableItem: {map.trackableItem?.name}</div>
                                        </>
                                    ) : ''}
                                    <button
                                        className="btn btn-sm btn-outline-primary mt-2"
                                        onClick={() => {
                                            setProductMap(map);
                                            setIsModalOpen(true);
                                        }}
                                    >
                                        Change Product
                                    </button>
                                </td>
                                <td className="border border-gray-300 px-4 py-2">
                                    {map.product && map.product.imageUrl ? (
                                        <img src={map.product.imageUrl} alt={map.product.name} className="w-20 h-20 object-contain" />
                                    ) : ''}
                                </td>
                                <td className="border border-gray-300 px-4 py-2">
                                    <CurrencyFormatter priceInCents={map.product ? map.product.priceInCents : 0} />
                                </td>
                                <td className="border border-gray-300 px-4 py-2">

                                    {map.trackableItem &&
                                        <>
                                            <div>{trackableItemStateMap[map.trackableItem?.state || 0]}</div><button
                                                className="btn btn-primary mt-3"
                                                onClick={() => {
                                                    setCurrentState(map.trackableItem?.state || TrackableItemState.READY);
                                                    setSelectedItemId(map.trackableItem?.uuid || null);
                                                    setShowModal(true);
                                                }}
                                            >
                                                Update State
                                            </button>
                                        </>}
                                </td>
                                <td className="border border-gray-300 px-2 py-2 text-blue-500 hover:underline cursor-pointer">
                                    <Link
                                        to={`/show-lease/${map.trackableItem?.inUseCustomer?.uuid}`}
                                        onClick={() => handleShowleasesClick(map.trackableItem?.inUseCustomer)}
                                        className="text-blue-500 hover:underline"
                                    >
                                        {map.trackableItem?.inUseCustomer?.mobileNumber}
                                    </Link>
                                    {map.trackableItem?.inUseSinceAt ? (
                                        <div className="text-sm text-black">
                                            <div><RelativeTime timestamp={map.trackableItem.inUseSinceAt} /></div>
                                        </div>
                                    ) : ''}
                                </td>
                                <td className="border border-gray-300 px-4 py-2">
                                    <div className="text-sm">
                                        {map.createdAt && (
                                            <div><span className="text-gray-500">Created:</span> <RelativeTime timestamp={map.createdAt} /></div>
                                        )}
                                        {map.updatedAt && map.createdAt && map.updatedAt.seconds !== map.createdAt.seconds && (
                                            <div><span className="text-gray-500">Updated:</span> <RelativeTime timestamp={map.updatedAt} /></div>
                                        )}
                                    </div>
                                </td>
                                <td className="border border-gray-300 px-4 py-2">
                                    <div className="flex gap-3 mb-2">
                                        <button
                                            className="btn btn-primary"
                                            onClick={() => {
                                                setSelectOpenItem(map);
                                            }}
                                        >
                                            Open Door
                                        </button>
                                    </div>
                                    <div>
                                        <button
                                            className="text-blue-600 hover:text-blue-800 underline text-sm bg-transparent border-0 p-0 cursor-pointer"
                                            onClick={() => {
                                                navigate(`/cell-transactions/${machine?.uuid}/${map.mdbItemNumber}`, {
                                                    state: { 
                                                        machine, 
                                                        productMap: map,
                                                        doorIdDisplay: map.doorIdDisplay 
                                                    }
                                                });
                                            }}
                                        >
                                            View Past Transactions
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>

            {/* Virtual Products Section */}
            {vpEnabled && vpMode && (
                <div className="mt-6" data-testid="virtual-products-section">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                                <Package className="w-5 h-5 text-indigo-600" />
                                <h3 className="text-lg font-semibold text-gray-900">Virtual Products</h3>
                            </div>
                            {vpMode === 'native' && machineConfigs.length > 0 && (
                                <span className="text-sm text-gray-500">
                                    {enabledVariantUuids.size} of {machineConfigs.length} enabled
                                </span>
                            )}
                            {vpMode === 'external' && (
                                <Link
                                    to="/virtual-products"
                                    className="text-sm text-indigo-600 hover:text-indigo-800 transition-colors"
                                >
                                    Manage Virtual Products
                                </Link>
                            )}
                        </div>

                        {loadingVirtualProducts ? (
                            <div className="flex items-center justify-center py-8">
                                <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
                            </div>
                        ) : vpMode === 'native' ? (
                            // Native mode: checkboxes grouped by category
                            machineConfigs.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                    <Package className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                                    <p className="text-sm">No virtual product variants configured. Create categories and variants on the <Link to="/virtual-products" className="text-indigo-600 hover:text-indigo-800 underline">Virtual Products</Link> page first.</p>
                                </div>
                            ) : (
                                <>
                                    <div className="space-y-4">
                                        {Object.entries(
                                            machineConfigs.reduce<Record<string, MachineVariantConfig[]>>((acc, config) => {
                                                const category = config.categoryName || 'Uncategorized';
                                                if (!acc[category]) acc[category] = [];
                                                acc[category].push(config);
                                                return acc;
                                            }, {})
                                        ).map(([categoryName, variants]) => (
                                            <div key={categoryName} className="border border-gray-200 rounded-lg overflow-hidden">
                                                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                                                    <h4 className="text-sm font-semibold text-gray-700">{categoryName}</h4>
                                                </div>
                                                <div className="divide-y divide-gray-100">
                                                    {variants.map((config) => (
                                                        <label
                                                            key={config.variantUuid}
                                                            className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
                                                        >
                                                            <div className="flex items-center gap-3">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={enabledVariantUuids.has(config.variantUuid)}
                                                                    onChange={() => toggleVariantEnabled(config.variantUuid)}
                                                                    className="form-checkbox h-4 w-4 text-indigo-600 rounded"
                                                                    data-testid={`vp-toggle-${config.variantUuid}`}
                                                                />
                                                                <div>
                                                                    <p className="text-sm font-medium text-gray-900">{config.variantName}</p>
                                                                    {config.variantSku && (
                                                                        <p className="text-xs text-gray-500 font-mono">{config.variantSku}</p>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <span className="text-sm font-medium text-gray-700">
                                                                <CurrencyFormatter priceInCents={config.priceInCents} />
                                                            </span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="mt-4">
                                        <button
                                            onClick={handleSaveVirtualProductConfigs}
                                            disabled={savingVirtualProducts}
                                            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                            data-testid="vp-save-btn"
                                        >
                                            {savingVirtualProducts ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                            Save Virtual Product Configuration
                                        </button>
                                    </div>
                                </>
                            )
                        ) : (
                            // External mode: read-only accordion
                            externalCategories.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                    <Package className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                                    <p className="text-sm">No virtual products found from external API.</p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {externalCategories.map((category) => (
                                        <div key={category.name} className="border border-gray-200 rounded-lg overflow-hidden">
                                            <button
                                                onClick={() => toggleExternalCategory(category.name)}
                                                className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                                            >
                                                <div className="flex items-center gap-3">
                                                    {category.imageUrl && (
                                                        <img src={category.imageUrl} alt={category.name} className="w-10 h-10 rounded-lg object-cover"
                                                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                                    )}
                                                    <div>
                                                        <p className="font-medium text-gray-900">{category.name}</p>
                                                        {category.description && <p className="text-sm text-gray-500">{category.description}</p>}
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-sm text-gray-500">
                                                        {category.variants.length} {category.variants.length === 1 ? 'variant' : 'variants'}
                                                    </span>
                                                    {expandedExternalCategories.has(category.name) ? (
                                                        <ChevronDown className="w-4 h-4 text-gray-400" />
                                                    ) : (
                                                        <ChevronRight className="w-4 h-4 text-gray-400" />
                                                    )}
                                                </div>
                                            </button>
                                            {expandedExternalCategories.has(category.name) && category.variants.length > 0 && (
                                                <div className="border-t border-gray-200">
                                                    <table className="w-full">
                                                        <thead>
                                                            <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                                <th className="px-4 py-2">SKU</th>
                                                                <th className="px-4 py-2">Name</th>
                                                                <th className="px-4 py-2">Description</th>
                                                                <th className="px-4 py-2 text-right">Price</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-gray-100">
                                                            {category.variants.map((variant) => (
                                                                <tr key={variant.sku} className="hover:bg-gray-50">
                                                                    <td className="px-4 py-2 text-sm font-mono text-gray-600">{variant.sku}</td>
                                                                    <td className="px-4 py-2 text-sm font-medium text-gray-900">{variant.name}</td>
                                                                    <td className="px-4 py-2 text-sm text-gray-500">{variant.description || '-'}</td>
                                                                    <td className="px-4 py-2 text-sm text-right font-medium">
                                                                        <CurrencyFormatter priceInCents={variant.priceInCents} />
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )
                        )}
                    </div>
                </div>
            )}

            {/* State Update Modal */}
            <StateUpdateModal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                currentState={currentState}
                onUpdate={handleUpdateState}
            />

            <BindingProductAndTackableItemModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                refreshTable={handleRefreshTable}
                productMap={productMap}
            />

            <ConfirmModal
                isOpen={selectOpenItem != null}
                onClose={() => {
                setSelectOpenItem(null)
            }}
                onConfirm={() => {
                    if(selectOpenItem){
                        openDoor(selectOpenItem)
                    }else{
                        toast.error('Please select an item');
                    }
                }}
                title={"Please Confirm"}
                content={`Are you sure you want to open door ${selectOpenItem?.doorIdDisplay}?`}
            />
        </div>
    );
};

export default LockerDetails;
